import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, Clock, Award, TrendingUp, TrendingDown, User, Briefcase, FileText } from 'lucide-react';
import { API_URL } from '../config';

const FicheRHEmployee = ({ employeeId, authToken, authUser, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadFiche = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/employees/${employeeId}/fiche-rh`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error('Erreur chargement');
      setData(await res.json());
    } catch (err) {
      console.error('Erreur chargement fiche RH:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken, employeeId]);

  useEffect(() => { loadFiche(); }, [loadFiche]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
        <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl bg-surface p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
        <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl bg-surface p-8 text-center">
          <p className="text-red-500">Impossible de charger la fiche RH</p>
          <button onClick={onClose} className="mt-4 w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-gray-200 rounded-lg font-medium">Fermer</button>
        </div>
      </div>
    );
  }

  const { employee, conges, modulation, pointages_mois, competences } = data;
  const solde = conges.solde;
  const mod = modulation.mois_courant;
  const pctCP = solde.total_acquis > 0 ? (solde.pris / solde.total_acquis) * 100 : 0;

  const formatHeures = (h) => {
    if (h === 0) return '0h';
    const sign = h >= 0 ? '+' : '-';
    const abs = Math.abs(h);
    const hours = Math.floor(abs);
    const mins = Math.round((abs - hours) * 60);
    return `${sign}${hours}h${mins > 0 ? String(mins).padStart(2, '0') : ''}`;
  };

  const dateEmbauche = employee.date_embauche ? new Date(employee.date_embauche).toLocaleDateString('fr-FR') : '—';
  const anciennete = solde.anciennete_annees !== undefined ? `${solde.anciennete_annees} an${solde.anciennete_annees > 1 ? 's' : ''}` : '—';

  const getNiveauColor = (niveau) => {
    switch (niveau) {
      case 'debutant': return 'bg-elevated-1 text-text-secondary';
      case 'intermediaire': return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';
      case 'confirme': return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
      case 'expert': return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
      default: return 'bg-elevated-1 text-text-secondary';
    }
  };

  const typeLabel = (t) => {
    const map = { conges_payes: 'CP', rtt: 'RTT', maladie: 'Maladie', conges_sans_solde: 'Sans solde' };
    return map[t] || t;
  };

  const statutColor = (s) => {
    if (s === 'accepte') return 'text-green-600 dark:text-green-400';
    if (s === 'refuse') return 'text-red-600 dark:text-red-400';
    return 'text-orange-500';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="w-full sm:max-w-3xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-surface">
        <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 sm:px-6 py-3 sm:py-4 shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center text-base sm:text-xl font-bold flex-shrink-0">
                {employee.first_name[0]}{employee.last_name[0]}
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold truncate">{employee.first_name} {employee.last_name}</h2>
                <p className="text-blue-100 text-xs sm:text-sm truncate">
                  {employee.service_name} · {employee.role_name} · {employee.heures_hebdomadaires}h/sem
                </p>
                <p className="text-blue-200 text-[10px] sm:text-xs mt-0.5 truncate">
                  Embauché le {dateEmbauche} · Ancienneté : {anciennete}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition p-2 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
          {/* Cartes résumé */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="bg-teal-50 dark:bg-teal-950/40 border border-teal-200 rounded-xl p-3 sm:p-4 text-center">
              <Calendar className="w-5 h-5 text-teal-600 dark:text-teal-400 mx-auto mb-1" />
              <p className="text-xl sm:text-2xl font-bold text-teal-700">{solde.restant}</p>
              <p className="text-xs text-teal-600 dark:text-teal-400">Jours CP restants</p>
            </div>
            <div className={`border rounded-xl p-4 text-center ${modulation.compteur_actuel >= 0 ? 'bg-green-50 dark:bg-green-950/40 border-green-200' : 'bg-red-50 dark:bg-red-950/40 border-red-200'}`}>
              {modulation.compteur_actuel >= 0
                ? <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
                : <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
              }
              <p className={`text-2xl font-bold ${modulation.compteur_actuel >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {formatHeures(modulation.compteur_actuel)}
              </p>
              <p className="text-xs text-text-secondary">Modulation</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 rounded-xl p-4 text-center">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-700">{pointages_mois.heures_totales}h</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">{pointages_mois.jours_pointes}j pointés ce mois</p>
            </div>
          </div>

          {/* Congés payés */}
          <div className="border border-border-subtle rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-teal-600 dark:text-teal-400" />
              <h3 className="font-semibold text-text-primary">Congés payés</h3>
              <span className="text-xs text-text-muted ml-auto">
                Période : {solde.periode_debut} — {solde.periode_fin}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-center text-xs sm:text-sm">
              <div className="bg-elevated-1 rounded-lg p-2">
                <p className="font-bold text-text-secondary">{solde.acquis}</p>
                <p className="text-xs text-text-muted">Acquis</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-950/40 rounded-lg p-2">
                <p className="font-bold text-purple-700">+{solde.bonus_anciennete}</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">Bonus HCR</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-2">
                <p className="font-bold text-amber-700">{solde.pris}</p>
                <p className="text-xs text-amber-600 dark:text-amber-400">Pris</p>
              </div>
              <div className={`rounded-lg p-2 ${solde.restant < 5 ? 'bg-red-50 dark:bg-red-950/40' : solde.restant < 10 ? 'bg-orange-50 dark:bg-orange-950/40' : 'bg-green-50 dark:bg-green-950/40'}`}>
                <p className={`font-bold ${solde.restant < 5 ? 'text-red-700 dark:text-red-300' : solde.restant < 10 ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'}`}>{solde.restant}</p>
                <p className="text-xs text-text-secondary">Restant</p>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
              <div className={`h-2 rounded-full transition-all ${pctCP > 80 ? 'bg-red-500' : pctCP > 60 ? 'bg-orange-500' : 'bg-teal-500'}`}
                style={{ width: `${Math.min(pctCP, 100)}%` }} />
            </div>

            {conges.historique.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-text-muted mb-1">Historique récent :</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {conges.historique.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-text-secondary py-1 border-b border-gray-50">
                      <span>{new Date(c.date_debut).toLocaleDateString('fr-FR')} — {new Date(c.date_fin).toLocaleDateString('fr-FR')}</span>
                      <span className="px-1.5 py-0.5 bg-elevated-1 rounded text-xs">{typeLabel(c.type)}</span>
                      <span>{c.nombre_jours}j</span>
                      <span className={`font-medium ${statutColor(c.statut)}`}>
                        {c.statut === 'accepte' ? '✓' : c.statut === 'refuse' ? '✗' : '⏳'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Modulation */}
          <div className="border border-border-subtle rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-semibold text-text-primary">Modulation du temps de travail</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center text-xs sm:text-sm">
              <div className="bg-elevated-1 rounded-lg p-2">
                <p className="font-bold text-text-secondary">{mod.heuresContratMensuel || '—'}h</p>
                <p className="text-xs text-text-muted">Contrat mois</p>
              </div>
              <div className="bg-elevated-1 rounded-lg p-2">
                <p className="font-bold text-text-secondary">{mod.heuresPointees !== undefined ? `${parseFloat(mod.heuresPointees).toFixed(1)}h` : '—'}</p>
                <p className="text-xs text-text-muted">Pointées ce mois</p>
              </div>
              <div className={`rounded-lg p-2 ${(mod.soldeMensuel || 0) >= 0 ? 'bg-green-50 dark:bg-green-950/40' : 'bg-red-50 dark:bg-red-950/40'}`}>
                <p className={`font-bold ${(mod.soldeMensuel || 0) >= 0 ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {mod.soldeMensuel !== undefined ? formatHeures(mod.soldeMensuel) : '—'}
                </p>
                <p className="text-xs text-text-muted">Solde mensuel</p>
              </div>
            </div>
          </div>

          {/* Compétences */}
          {competences.length > 0 && (
            <div className="border border-border-subtle rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <h3 className="font-semibold text-text-primary">Compétences</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {competences.map((c, i) => (
                  <span key={i} className={`px-3 py-1.5 rounded-full text-xs font-medium ${getNiveauColor(c.niveau)}`}>
                    {c.nom || c.competence}
                    {c.certifie ? ' ✓' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Footer sticky */}
        <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
          <button
            onClick={onClose}
            className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center border hover:bg-elevated-1 transition"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default FicheRHEmployee;
